<template>
  <div>
    <!-- Comment Dialog -->
    <v-dialog :value="commentDialog" @input="$emit('update:commentDialog', $event)" max-width="700px" scrollable>
      <v-card class="comment-dialog">
        <!-- Header -->
        <v-card-title class="primary white--text py-3">
          <v-icon dark class="mr-2">mdi-comment-text-multiple</v-icon>
          <span>Commentaires</span>
          <v-spacer />
          <v-chip v-if="comments && comments.length > 0" color="white" text-color="primary" small>
            {{ comments.length }}
          </v-chip>
          <v-btn icon dark @click="$emit('update:commentDialog', false)" class="ml-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Alert si non disponible -->
        <v-alert
          v-if="!saveCommentEnabled"
          type="warning"
          dense
          class="ma-0 rounded-0"
          icon="mdi-alert-circle"
        >
          Cette machine n'est pas enregistrée dans Nuage. Les commentaires ne sont pas disponibles.
        </v-alert>

        <!-- Liste des commentaires -->
        <v-card-text class="comments-container pa-0" style="max-height: 400px; overflow-y: auto;">
          <!-- État vide -->
          <div v-if="!comments || comments.length === 0" class="empty-state text-center pa-8">
            <v-icon size="64" color="grey lighten-1">mdi-comment-outline</v-icon>
            <div class="text-h6 grey--text mt-4">Aucun commentaire</div>
            <div class="text-body-2 grey--text text--lighten-1">Soyez le premier à laisser un commentaire</div>
          </div>

          <!-- Liste des commentaires -->
          <div v-else class="pa-4">
            <div
              v-for="(comment, index) in sortedComments"
              :key="index"
              class="comment-item mb-4"
            >
              <div class="d-flex align-start">
                <!-- Avatar -->
                <v-avatar
                  :color="getAvatarColor(comment.author)"
                  size="40"
                  class="mr-3 flex-shrink-0"
                >
                  <span class="white--text font-weight-medium">
                    {{ getInitials(comment.author) }}
                  </span>
                </v-avatar>

                <!-- Contenu du commentaire -->
                <div class="comment-content flex-grow-1">
                  <div class="comment-bubble pa-3">
                    <div class="d-flex align-center mb-1">
                      <span class="font-weight-bold text-body-2">{{ comment.author }}</span>
                      <v-spacer />
                      <span class="text-caption grey--text">
                        <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                        {{ formatDateRelative(comment.date) }}
                      </span>
                    </div>
                    <div class="comment-text text-body-2">{{ comment.content }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <!-- Zone de saisie -->
        <v-card-text class="pa-4 input-area">
          <div class="d-flex align-end">
            <v-textarea
              :value="currentComment"
              @input="$emit('update:currentComment', $event)"
              placeholder="Écrivez votre commentaire..."
              rows="2"
              auto-grow
              outlined
              dense
              hide-details
              :disabled="!saveCommentEnabled"
              class="comment-input flex-grow-1 mr-3"
              @keydown.ctrl.enter="handleSendComment"
            />
            <v-btn
              color="primary"
              fab
              small
              :disabled="!saveCommentEnabled || !currentComment || currentComment.trim() === ''"
              @click="handleSendComment"
              class="send-btn"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </div>
          <div class="text-caption grey--text mt-2">
            <v-icon x-small class="mr-1">mdi-keyboard</v-icon>
            Ctrl + Entrée pour envoyer
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Refresh Dialog -->
    <!-- ...existing code... -->
    <v-dialog :value="refreshDialog" @input="$emit('update:refreshDialog', $event)" max-width="600px">
      <v-card>
        <v-card-title class="headline">Confirmation</v-card-title>
        <v-card-text>
          Un rapport est généré automatiquement de manière quotidienne à 3 heures du matin, êtes vous sur de vouloir lancer la génération d'un rapport ? <br><br>
          La génération d'un nouveau rapport peut prendre quelques minutes
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="$emit('update:refreshDialog', false)">Annuler</v-btn>
          <v-btn text @click="$emit('confirm-refresh')">Confirmer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Tools Status Dialog -->
    <v-dialog :value="infoDialog" @input="$emit('update:infoDialog', $event)" max-width="500">
      <v-card>
        <v-card-title class="headline">Etat des outils</v-card-title>
        <v-card-text>
          <div v-if="loadingStatuses">
            <v-progress-circular indeterminate color="primary" size="20" />
          </div>
          <v-list>
            <v-list-item v-for="api in apiStatuses" :key="api.name">
              <v-list-item-content>
                <div class="d-flex justify-space-between align-center">
                  <span>{{ api.name }}</span>
                  <v-chip :color="api.status === 'En ligne' ? 'green' : 'red'" dark>
                    {{ api.status }}
                  </v-chip>
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="$emit('update:infoDialog', false)">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog :value="errorDialog" @input="$emit('update:errorDialog', $event)" max-width="600px">
      <v-card>
        <v-card-title class="headline">Erreur inattendue</v-card-title>
        <v-card-text>
          Une erreur inattendue s'est produite, merci de réessayer plus tard
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-grey" text @click="$emit('update:errorDialog', false)">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'DashboardDialogs',
  props: {
    // Comment dialog
    commentDialog: Boolean,
    currentComment: String,
    comments: Array,
    saveCommentEnabled: Boolean,
    // Refresh dialog
    refreshDialog: Boolean,
    // Info dialog
    infoDialog: Boolean,
    apiStatuses: Array,
    loadingStatuses: Boolean,
    // Error dialog
    errorDialog: Boolean
  },
  computed: {
    sortedComments() {
      if (!this.comments) return [];
      return [...this.comments].sort((a, b) => new Date(b.date) - new Date(a.date));
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr || dateStr === 'N/A') return 'N/A';
      const date = new Date(dateStr);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    formatDateRelative(dateStr) {
      if (!dateStr || dateStr === 'N/A') return 'N/A';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "À l'instant";
      if (diffMins < 60) return `Il y a ${diffMins} min`;
      if (diffHours < 24) return `Il y a ${diffHours}h`;
      if (diffDays < 7) return `Il y a ${diffDays}j`;

      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },

    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    getAvatarColor(name) {
      if (!name) return 'grey';
      const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'error', 'purple', 'teal', 'indigo', 'deep-orange'];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },

    handleSendComment() {
      if (this.saveCommentEnabled && this.currentComment && this.currentComment.trim() !== '') {
        this.$emit('save-comment');
      }
    }
  }
};
</script>

<style scoped>
.comment-dialog {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}

.comments-container {
  flex: 1;
  overflow-y: auto;
  background-color: #f5f5f5;
}

.empty-state {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.comment-item {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.comment-bubble {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.comment-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.input-area {
  background-color: white;
}

.comment-input >>> .v-input__slot {
  background-color: #f5f5f5 !important;
}

.send-btn {
  margin-bottom: 4px;
}
</style>

